/**
 * The firm's library as the screen sees it: every entry (active and in
 * intake), the batches still in flight, and the legacy-import status. Polls
 * while the server is still preparing something so renderings, the agent's
 * reads, self-activation and batch progress show up without a reload.
 */
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";

import { openBatches, shouldPoll, sortEntries } from "../model";
import type {
  FirmLibraryBatch,
  FirmLibraryEntry,
  FirmLibraryFolder,
  MigrationStatus
} from "../types";

const POLL_MS = 4000;

export type FirmLibraryState = {
  entries: FirmLibraryEntry[];
  /** Active entries, newest first: what the agents see. */
  active: FirmLibraryEntry[];
  /**
   * Every row the library lists, newest first: active entries, uploads
   * still in intake (stored, being read, or waiting for a description) and
   * ones that failed, so they can be retried or removed. Nothing is hidden
   * behind a review step.
   */
  visible: FirmLibraryEntry[];
  /** The firm's folder tree, flat. */
  folders: FirmLibraryFolder[];
  batches: FirmLibraryBatch[];
  migration: MigrationStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Swap one entry in place after a mutation returned the fresh row. */
  replaceEntry: (entry: FirmLibraryEntry) => void;
  /** Swap several entries at once (a move, an undo). */
  replaceEntries: (entries: FirmLibraryEntry[]) => void;
  removeEntry: (entryId: string) => void;
  /** Add or swap a folder after the server returned it. */
  applyFolder: (folder: FirmLibraryFolder) => void;
  /** Drop deleted folders and unfile what was in them. */
  removeFolders: (folderIds: string[]) => void;
};

export const useFirmLibrary = (scope: FirmScope): FirmLibraryState => {
  const [entries, setEntries] = useState<FirmLibraryEntry[]>([]);
  const [folders, setFolders] = useState<FirmLibraryFolder[]>([]);
  const [batches, setBatches] = useState<FirmLibraryBatch[]>([]);
  const [migration, setMigration] = useState<MigrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const scopeKey = scope.lawFirmId ?? "";

  const refresh = useCallback(async () => {
    try {
      const data = await firmKnowledgeApi.listLibrary(
        scopeKey ? { lawFirmId: scopeKey } : {},
        "all"
      );
      if (!alive.current) return;
      setEntries(data.entries);
      setFolders(data.folders ?? []);
      setBatches(data.open_batches);
      setMigration(data.migration);
      setError(null);
    } catch (err) {
      Sentry.captureException(err);
      if (!alive.current) return;
      setError(
        err instanceof Error ? err.message : "Could not load the library."
      );
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [scopeKey]);

  useEffect(() => {
    alive.current = true;
    setLoading(true);
    void refresh();
    return () => {
      alive.current = false;
    };
  }, [refresh]);

  const polling = useMemo(
    () => shouldPoll({ entries, batches, migration }),
    [entries, batches, migration]
  );
  useEffect(() => {
    if (!polling) return undefined;
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [polling, refresh]);

  const replaceEntry = useCallback((entry: FirmLibraryEntry) => {
    setEntries((current) => {
      const index = current.findIndex((e) => e.id === entry.id);
      if (index < 0) return [entry, ...current];
      const next = current.slice();
      next[index] = entry;
      return next;
    });
  }, []);

  const replaceEntries = useCallback((fresh: FirmLibraryEntry[]) => {
    const byId = new Map(fresh.map((e) => [e.id, e]));
    setEntries((current) => current.map((e) => byId.get(e.id) ?? e));
  }, []);

  const removeEntry = useCallback((entryId: string) => {
    setEntries((current) => current.filter((e) => e.id !== entryId));
  }, []);

  const applyFolder = useCallback((folder: FirmLibraryFolder) => {
    setFolders((current) =>
      current.some((f) => f.id === folder.id)
        ? current.map((f) => (f.id === folder.id ? folder : f))
        : [...current, folder]
    );
  }, []);

  const removeFolders = useCallback((folderIds: string[]) => {
    const gone = new Set(folderIds);
    setFolders((current) => current.filter((f) => !gone.has(f.id)));
    setEntries((current) =>
      current.map((e) =>
        e.folder_id && gone.has(e.folder_id) ? { ...e, folder_id: null } : e
      )
    );
  }, []);

  const open = useMemo(() => openBatches(batches), [batches]);
  const active = useMemo(
    () => sortEntries(entries.filter((e) => e.status === "active")),
    [entries]
  );
  const visible = useMemo(
    () =>
      sortEntries(
        entries.filter(
          (e) =>
            e.status === "active" ||
            e.status === "pending_review" ||
            e.status === "failed"
        )
      ),
    [entries]
  );

  return {
    entries,
    active,
    visible,
    folders,
    batches: open,
    migration,
    loading,
    error,
    refresh,
    replaceEntry,
    replaceEntries,
    removeEntry,
    applyFolder,
    removeFolders
  };
};
